// How the writing view is shown beyond the basics: which bars stay pinned
// open, and the writer's own "default page" that Ctrl+Shift+0 returns to.

export type PinnableBar = 'top' | 'bottom' | 'right' | 'left'

export const PINNABLE_BARS: { id: PinnableBar; label: string }[] = [
  { id: 'top', label: 'Top bar' },
  { id: 'bottom', label: 'Bottom bar' },
  { id: 'right', label: 'Right bar' },
  { id: 'left', label: 'Corpus panel' },
]

/** The page as the writer likes it: text size, column width and line spacing. */
export interface DefaultPage {
  fontSize: number
  width: number
  widthUnit: 'ch' | 'px'
  lineHeight: number
}

export interface ViewPrefs {
  /** The pin key (Ctrl+`) has pinned the bars open. */
  barsPinned: boolean
  /** Which bars the pin key covers. */
  pinnedBars: PinnableBar[]
  /** Null until the writer saves one; the app's own defaults stand in. */
  defaultPage: DefaultPage | null
}

export const APP_DEFAULT_PAGE: DefaultPage = { fontSize: 20, width: 65, widthUnit: 'ch', lineHeight: 1.6 }

export const DEFAULT_VIEW_PREFS: ViewPrefs = {
  barsPinned: false,
  pinnedBars: ['top', 'bottom'],
  defaultPage: null,
}

function positive(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : null
}

function normalizeDefaultPage(raw: any): DefaultPage | null {
  if (!raw || typeof raw !== 'object') return null
  const fontSize = positive(raw.fontSize, 6, 1000)
  const width = positive(raw.width, 1, 10000)
  const lineHeight = positive(raw.lineHeight, 0.5, 5)
  if (fontSize === null || width === null || lineHeight === null) return null
  return { fontSize, width, widthUnit: raw.widthUnit === 'px' ? 'px' : 'ch', lineHeight }
}

export function normalizeViewPrefs(raw: any): ViewPrefs {
  const ids = PINNABLE_BARS.map((bar) => bar.id)
  const pinned = Array.isArray(raw?.pinnedBars)
    ? ids.filter((id) => raw.pinnedBars.includes(id))
    : [...DEFAULT_VIEW_PREFS.pinnedBars]
  return {
    barsPinned: raw?.barsPinned === true,
    pinnedBars: pinned,
    defaultPage: normalizeDefaultPage(raw?.defaultPage),
  }
}
